import { setPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { commitMutation, fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { budgetsPage_Query, budgetsPage_Query$data } from "@app/queries/budgetsPage_Query.graphql";
import type { budgetsPage_DeleteMutation } from "@app/queries/budgetsPage_DeleteMutation.graphql";

type BudgetRow = budgetsPage_Query$data["budgets"][number];

export const budgetsListQuery = graphql`
  query budgetsPage_Query {
    budgets {
      id
      name
      side
      year
      basis
      entryCount
      totalCents
      updatedAt
      perspective {
        id
        name
      }
    }
  }
`;

const deleteMutation = graphql`
  mutation budgetsPage_DeleteMutation($input: BudgetDeleteInputObject!) {
    budgetDelete(input: $input) {
      budget {
        id
      }
      errors {
        message
      }
    }
  }
`;

const money = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const lens = (budget: BudgetRow) => {
  if (budget.side !== "COST") return "—";
  const parts = [budget.perspective?.name, budget.basis].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
};

// The /budgets page: every saved "Actuals vs Planned" budget across both sides and
// all years. Open routes to the right overview grid with the budget loaded (cost
// budgets also restore their perspective lens); Delete removes it.
export default function BudgetsPage({ queryRef }: { queryRef: PreloadedQuery<budgetsPage_Query> }) {
  const preloaded = usePreloadedQuery(budgetsListQuery, queryRef);
  const [budgets, setBudgets] = useState(preloaded.budgets);
  const navigate = useNavigate();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setBudgets(preloaded.budgets), [queryRef]);

  const refresh = () =>
    fetchQuery<budgetsPage_Query>(environment, budgetsListQuery, {}, { fetchPolicy: "network-only" })
      .toPromise()
      .then((data) => {
        if (data) setBudgets(data.budgets);
      });

  const open = (budget: BudgetRow) => {
    if (budget.side === "COST" && budget.perspective) setPerspectiveId(budget.perspective.id);
    navigate({
      to: budget.side === "COST" ? "/categorization-overview" : "/revenue",
      search: { budget: budget.id },
    });
  };

  const remove = (budget: BudgetRow) => {
    if (!window.confirm(`Delete budget "${budget.name}"? This cannot be undone.`)) return;
    commitMutation<budgetsPage_DeleteMutation>(environment, {
      mutation: deleteMutation,
      variables: { input: { id: budget.id } },
      onCompleted: (_response, errors) => (errors?.length ? window.alert(errors[0].message) : refresh()),
      onError: (error) => window.alert(error.message),
    });
  };

  return (
    <div className="p-6">
      <h1 className="text-xl font-bold mb-1">Budgets</h1>
      <p className="text-sm text-gray-500 mb-4">
        Saved “Actuals vs Planned” budgets. Open one to edit it on its overview grid, or delete it.
      </p>

      {budgets.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded-lg bg-white p-10 text-center">
          <p className="text-sm text-gray-500">
            No budgets yet. Turn on “Actuals vs Planned” on the Revenue or Categorization overview page,
            type some planned amounts, and save.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 uppercase border-b border-gray-200 text-left">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Side</th>
                <th className="px-3 py-2 font-medium">Year</th>
                <th className="px-3 py-2 font-medium">Lens</th>
                <th className="px-3 py-2 font-medium text-right">Cells</th>
                <th className="px-3 py-2 font-medium text-right">Total</th>
                <th className="px-3 py-2 font-medium">Updated</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {budgets.map((budget) => (
                <tr key={budget.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60">
                  <td className="px-3 py-2 font-medium text-gray-800">{budget.name}</td>
                  <td className="px-3 py-2 text-gray-600">{budget.side === "COST" ? "Cost" : "Revenue"}</td>
                  <td className="px-3 py-2 tabular-nums text-gray-600">{budget.year}</td>
                  <td className="px-3 py-2 text-gray-500">{lens(budget)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">{budget.entryCount}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-800">{money(budget.totalCents)}</td>
                  <td className="px-3 py-2 text-gray-500">{new Date(budget.updatedAt).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => open(budget)}
                      className="px-2 py-1 rounded border border-gray-200 text-emerald-700 hover:bg-emerald-50"
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(budget)}
                      className="ml-2 px-2 py-1 rounded border border-gray-200 text-rose-600 hover:bg-rose-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
